/**
 * Universal Location Service for Mobile & Web
 * Fetches current device/browser coordinates and reverse-geocodes into a human-readable venue or city.
 */

export const detectCurrentLocation = async () => {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({ success: false, error: 'Geolocation is not supported on this device/browser.' });
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000,
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;

          // 1. Try reverse geocoding via BigDataCloud client API (fast, CORS-friendly, free, no key needed)
          try {
            const bdcUrl = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`;
            const res = await fetch(bdcUrl);
            if (res.ok) {
              const data = await res.json();
              const city = data.city || data.locality || data.principalSubdivision || '';
              const state = data.principalSubdivision || '';
              const country = data.countryCode || data.countryName || '';

              const parts = [];
              if (city) parts.push(city);
              if (state && state !== city) parts.push(state);
              if (country) parts.push(country);

              if (parts.length > 0) {
                resolve({
                  success: true,
                  location: parts.join(', '),
                  coords: { latitude: lat, longitude: lon },
                });
                return;
              }
            }
          } catch (_e) {}

          // 2. Fallback to OpenStreetMap Nominatim
          try {
            const nomUrl = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=14&addressdetails=1`;
            const res = await fetch(nomUrl, {
              headers: { 'Accept-Language': 'en' },
            });
            if (res.ok) {
              const data = await res.json();
              const addr = data.address || {};
              const venue = addr.leisure || addr.club || addr.sports_centre || addr.park || '';
              const city = addr.city || addr.town || addr.village || addr.suburb || addr.county || '';
              const state = addr.state || '';
              const country = addr.country || '';

              const parts = [];
              if (venue) parts.push(venue);
              if (city && city !== venue) parts.push(city);
              if (state && state !== city) parts.push(state);
              else if (country && parts.length < 2) parts.push(country);

              if (parts.length > 0) {
                resolve({
                  success: true,
                  location: parts.join(', '),
                  coords: { latitude: lat, longitude: lon },
                });
                return;
              }
            }
          } catch (_e) {}

          // Fallback to formatted coordinates if geocoding APIs fail
          resolve({
            success: true,
            location: `${lat.toFixed(4)}, ${lon.toFixed(4)}`,
            coords: { latitude: lat, longitude: lon },
          });
        } catch (err) {
          resolve({ success: false, error: err.message || 'Reverse geocoding failed.' });
        }
      },
      (error) => {
        let msg = 'Unable to retrieve location.';
        if (error.code === 1) msg = 'Location access was denied.';
        else if (error.code === 2) msg = 'Location unavailable.';
        else if (error.code === 3) msg = 'Location request timed out.';
        resolve({ success: false, error: msg });
      },
      options
    );
  });
};
